/**
 * Pure time-phased financial engine (Planisware FIN-004 / FIN-AT-02 / FIN-AT-03).
 * Allocates launch cost plans onto calendar buckets; compares to funding envelopes.
 */

import { generateTimeBuckets } from './planningEngine';
import type { TimePhasedPlacement } from './timePhasedPlanning.types';
import type {
  BucketMoneyUtilization,
  CostDemandChunk,
  FundingAffordability,
  FundingEnvelope,
  PriorityCostPlan,
  TimePhasedFinancialOutput,
} from './timePhasedFinancial.types';

function addWeeks(isoDate: string, weeks: number): string {
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}

/** Snap a calendar date into the Mon–Sun bucket used by generateTimeBuckets. */
function snapToBucketStart(
  isoDate: string,
  horizonStart: string,
  horizonEnd: string,
): string {
  const buckets = generateTimeBuckets(horizonStart, horizonEnd, 'week');
  const hit = buckets.find(
    (b) => isoDate >= b.time_bucket_start && isoDate <= b.time_bucket_end,
  );
  return hit?.time_bucket_start ?? isoDate;
}

function resolvedForecast(plan: PriorityCostPlan): number {
  if (
    plan.laborHours != null &&
    plan.hourlyRate != null &&
    Number.isFinite(plan.laborHours) &&
    Number.isFinite(plan.hourlyRate)
  ) {
    return plan.laborHours * plan.hourlyRate;
  }
  return plan.forecastAmount ?? 0;
}

/** Whole dollars, as every funding surface writes them: "$1,250,000". */
export function formatDollars(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

/**
 * Expand a launch-relative cost plan across week buckets anchored at launch date.
 * FIN-AT-02: when launch moves, re-expand at new anchor → $ follows schedule.
 */
export function expandCostPlanAtAnchor(
  plan: PriorityCostPlan,
  priorityName: string,
  launchAnchor: string,
  horizonStart: string,
  horizonEnd: string,
): CostDemandChunk[] {
  const total = resolvedForecast(plan);
  if (total <= 0) return [];

  if (plan.phasingMode === 'absolute_periods' && plan.periods?.length) {
    return plan.periods
      .filter((p) => p.forecastAmount > 0)
      .filter((p) => p.bucketStart >= horizonStart && p.bucketStart <= horizonEnd)
      .map((p) => ({
        priorityId: plan.priorityId,
        priorityName,
        costPlanId: plan.id,
        costCategory: plan.costCategory,
        bucketStart: p.bucketStart,
        bucketEnd: p.bucketEnd,
        amount: p.forecastAmount,
      }));
  }

  const startOffset = Math.min(plan.offsetStartWeeks, plan.offsetEndWeeks);
  const endOffset = Math.max(plan.offsetStartWeeks, plan.offsetEndWeeks);
  const windowStart = addWeeks(launchAnchor, startOffset);
  const windowEnd = addWeeks(launchAnchor, endOffset);

  const buckets = generateTimeBuckets(
    windowStart < horizonStart ? horizonStart : windowStart,
    windowEnd > horizonEnd ? horizonEnd : addWeeks(windowEnd, 1),
    'week',
  ).filter((b) => b.time_bucket_start >= windowStart && b.time_bucket_start <= windowEnd);

  if (buckets.length === 0) return [];
  const per = total / buckets.length;
  return buckets.map((b) => ({
    priorityId: plan.priorityId,
    priorityName,
    costPlanId: plan.id,
    costCategory: plan.costCategory,
    bucketStart: b.time_bucket_start,
    bucketEnd: b.time_bucket_end,
    amount: Math.round(per * 100) / 100,
  }));
}

export function computeFinancialUtilization(
  envelopes: FundingEnvelope[],
  demandChunks: CostDemandChunk[],
  horizonStart: string,
  horizonEnd: string,
): BucketMoneyUtilization[] {
  const buckets = generateTimeBuckets(horizonStart, horizonEnd, 'week');
  const supplyByBucket = new Map<string, number>();
  for (const env of envelopes) {
    for (const p of env.periods) {
      supplyByBucket.set(
        p.bucketStart,
        (supplyByBucket.get(p.bucketStart) ?? 0) + (p.approvedAmount ?? 0),
      );
    }
  }
  const demandByBucket = new Map<string, number>();
  for (const c of demandChunks) {
    demandByBucket.set(c.bucketStart, (demandByBucket.get(c.bucketStart) ?? 0) + c.amount);
  }

  return buckets.map((b) => {
    const approvedSupply = supplyByBucket.get(b.time_bucket_start) ?? 0;
    const forecastDemand = demandByBucket.get(b.time_bucket_start) ?? 0;
    const overage = Math.max(0, forecastDemand - approvedSupply);
    return {
      bucketStart: b.time_bucket_start,
      bucketEnd: b.time_bucket_end,
      approvedSupply,
      forecastDemand,
      utilizationPct:
        approvedSupply > 0 ? Math.round((forecastDemand / approvedSupply) * 100) : forecastDemand > 0 ? 999 : 0,
      overage,
    };
  });
}

export function computeAffordability(
  moneyUtilization: BucketMoneyUtilization[],
  demandChunks: CostDemandChunk[],
): FundingAffordability {
  const totalApproved = moneyUtilization.reduce((s, b) => s + b.approvedSupply, 0);
  const totalForecastDemand = moneyUtilization.reduce((s, b) => s + b.forecastDemand, 0);
  const overageTotal = moneyUtilization.reduce((s, b) => s + b.overage, 0);
  const overloadedBuckets = moneyUtilization
    .filter((b) => b.overage > 0)
    .map((b) => ({
      bucketStart: b.bucketStart,
      overage: b.overage,
      demand: b.forecastDemand,
      approved: b.approvedSupply,
    }));

  const byPriority = new Map<string, { priorityId: string; priorityName: string; forecastDemand: number }>();
  const overloadedStarts = new Set(overloadedBuckets.map((b) => b.bucketStart));
  for (const c of demandChunks) {
    if (!overloadedStarts.has(c.bucketStart)) continue;
    const cur = byPriority.get(c.priorityId) ?? {
      priorityId: c.priorityId,
      priorityName: c.priorityName,
      forecastDemand: 0,
    };
    cur.forecastDemand += c.amount;
    byPriority.set(c.priorityId, cur);
  }
  const contributingPriorities = [...byPriority.values()].sort(
    (a, b) => b.forecastDemand - a.forecastDemand,
  );

  return {
    withinEnvelope: overageTotal <= 0.01,
    totalApproved,
    totalForecastDemand,
    overageTotal,
    contributingPriorities,
    overloadedBuckets,
  };
}

/**
 * Allocate cost plans onto placed launches (uses feasibleStart as launch anchor).
 */
export function runTimePhasedFinancialEngine(input: {
  horizonStart: string;
  horizonEnd: string;
  placements: TimePhasedPlacement[];
  costPlans: PriorityCostPlan[];
  envelopes: FundingEnvelope[];
}): TimePhasedFinancialOutput {
  const nameById = new Map(input.placements.map((p) => [p.priorityId, p.priorityName]));
  const anchorById = new Map(
    input.placements
      .filter((p) => p.feasibleStart)
      .map((p) => [p.priorityId, p.feasibleStart!]),
  );

  const demandChunks: CostDemandChunk[] = [];
  for (const plan of input.costPlans) {
    const rawAnchor = anchorById.get(plan.priorityId);
    if (!rawAnchor) continue;
    const anchor = snapToBucketStart(rawAnchor, input.horizonStart, input.horizonEnd);
    const name = nameById.get(plan.priorityId) ?? plan.priorityId;
    demandChunks.push(
      ...expandCostPlanAtAnchor(
        plan,
        name,
        anchor,
        input.horizonStart,
        input.horizonEnd,
      ),
    );
  }

  const moneyUtilization = computeFinancialUtilization(
    input.envelopes,
    demandChunks,
    input.horizonStart,
    input.horizonEnd,
  );
  const affordability = computeAffordability(moneyUtilization, demandChunks);

  return { moneyUtilization, affordability, demandChunks };
}
